import dayjs from 'dayjs';
import type { Reservation, TimeInformation } from '@/features/driver/types/driveDetail';

const processData = (records: Reservation[]) => {
  const timeline: Record<string, TimeInformation> = {};

  records.forEach((record: Reservation) => {
    const pickupKey = `${record.latestEstimatedPickupDatetime}_${record.pickupWaypointSequence}`;
    const dropoffKey = `${record.latestEstimatedDropoffDatetime}_${record.dropoffWaypointSequence}`;

    if (!timeline[pickupKey]) {
      timeline[pickupKey] = {
        time: record.latestEstimatedPickupDatetime,
        waypointId: record.pickupWaypointId,
        waypointName: `${record.pickupWaypointNumber}.${record.pickupWaypointName}`,
        latitude: record.pickupLatitude,
        longitude: record.pickupLongitude,
        waypointSequence: record.pickupWaypointSequence,
        pickup: [],
        dropoff: [],
      };
    }
    timeline[pickupKey].pickup.push(record);

    if (!timeline[dropoffKey]) {
      timeline[dropoffKey] = {
        time: record.latestEstimatedDropoffDatetime,
        waypointId: record.dropoffWaypointId,
        waypointName: `${record.dropoffWaypointNumber}.${record.dropoffWaypointName}`,
        latitude: record.dropoffLatitude,
        longitude: record.dropoffLongitude,
        waypointSequence: record.dropoffWaypointSequence,
        pickup: [],
        dropoff: [],
      };
    }
    timeline[dropoffKey].dropoff.push(record);
  });

  return Object.values(timeline)
    .filter((event) => event.time != null)
    .sort((a, b) => {
      if (dayjs(a.time).isSame(dayjs(b.time))) {
        return (Number(a.waypointSequence) || 0) - (Number(b.waypointSequence) || 0);
      }

      return a.time.localeCompare(b.time);
    });
};

// 乗降地点名編集
const formatWaypoint = (number: string, waypoint: string) => {
  if (number.includes('自宅')) {
    return `${number}.${waypoint}`;
  }

  if (waypoint.length > 10) {
    return `${number}.${waypoint.substring(0, 10)}…`;
  }

  return `${number}.${waypoint}`;
};

const processDataForReservationInfo = (records: Reservation[]) => {
  const timeline: Record<string, TimeInformation> = {};

  records.forEach((record: Reservation) => {
    const pickupKey = `${record.latestEstimatedPickupDatetime}_${record.pickupWaypointSequence}`;
    const dropoffKey = `${record.latestEstimatedDropoffDatetime}_${record.dropoffWaypointSequence}`;

    if (!timeline[pickupKey]) {
      timeline[pickupKey] = {
        time: record.latestEstimatedPickupDatetime,
        waypointId: record.pickupWaypointId,
        waypointName: formatWaypoint(record.pickupWaypointNumber, record.pickupWaypointName),
        latitude: record.pickupLatitude,
        longitude: record.pickupLongitude,
        waypointSequence: record.pickupWaypointSequence,
        pickup: [],
        dropoff: [],
      };
    }
    timeline[pickupKey].pickup.push(record);

    if (!timeline[dropoffKey]) {
      timeline[dropoffKey] = {
        time: record.latestEstimatedDropoffDatetime,
        waypointId: record.dropoffWaypointId,
        waypointName: formatWaypoint(record.dropoffWaypointNumber, record.dropoffWaypointName),
        latitude: record.dropoffLatitude,
        longitude: record.dropoffLongitude,
        waypointSequence: record.dropoffWaypointSequence,
        pickup: [],
        dropoff: [],
      };
    }
    timeline[dropoffKey].dropoff.push(record);
  });

  return Object.values(timeline)
    .filter((event) => event.time != null)
    .sort((a, b) => {
      if (dayjs(a.time).isSame(dayjs(b.time))) {
        return (Number(a.waypointSequence) || 0) - (Number(b.waypointSequence) || 0);
      }

      return a.time.localeCompare(b.time);
    });
};

export const getTimelineData = (records: Reservation[]) => {
  const timelineData = processData(records);

  let key = 0;
  const newData = timelineData.map((item, index, array) => {
    key += 1;
    if (index < array.length - 1) {
      const currentTime = new Date(item.time);
      const nextTime = new Date(array[index + 1].time);
      const timeDiff = dayjs(nextTime).diff(dayjs(currentTime), 'minute');

      return { ...item, timeDiff, key };
    }

    return { ...item, timeDiff: 0, key };
  });

  return newData;
};

export const getTimelineDataForReservationInfo = (records: Reservation[]) => {
  const timelineData = processDataForReservationInfo(records);

  let key = 0;
  const newData = timelineData.map((item, index, array) => {
    key += 1;
    if (index < array.length - 1) {
      const currentTime = new Date(item.time);
      const nextTime = new Date(array[index + 1].time);
      const timeDiff = dayjs(nextTime).diff(dayjs(currentTime), 'minute');

      return { ...item, timeDiff, key };
    }

    return { ...item, timeDiff: 0, key };
  });

  return newData;
};

export const formatMoney = (money?: number) => money?.toLocaleString() || '';
